import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import type { Product, EntityStatus } from "../../types";
import { EntityStatus as EntityStatusEnum } from "../../types";
import ProductRelationsPicker, {
  productToRelationsSelection,
  relationsSelectionToUuids,
  type ProductRelationsSelection,
} from "./ProductRelationsPicker";
import ProductGatewaySkuSection from "./ProductGatewaySkuSection";
import PmgAssignmentSection from "../product-management-groups/PmgAssignmentSection";
import DocumentFileUploadDialog from "./DocumentFileUploadDialog";
import { EntityType } from "../../types";
import UploadFileIcon from "@mui/icons-material/UploadFile";

interface EditProductDialogProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Product) => void;
}

const emptyRelations = (): ProductRelationsSelection => ({
  apis: [],
  categories: [],
  documents: [],
  faqs: [],
  links: [],
  developerGuides: [],
});

const EditProductDialog: React.FC<EditProductDialogProps> = ({
  product,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Product | null>(product);
  const [relations, setRelations] = useState<ProductRelationsSelection>(
    emptyRelations,
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pmgAccessDenied, setPmgAccessDenied] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const readOnly = pmgAccessDenied;

  useEffect(() => {
    if (!open || !product?.uuid) {
      setDraft(null);
      setRelations(emptyRelations());
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    apiService
      .getProduct(product.uuid)
      .then((full) => {
        if (!cancelled) {
          setDraft(full);
          setRelations(productToRelationsSelection(full));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error
              ? err.message
              : "Failed to load product details",
          );
          setDraft(product);
          setRelations(productToRelationsSelection(product));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, product]);

  const handleSave = async () => {
    if (!draft?.uuid) return;
    setSaving(true);
    try {
      const updated = await apiService.updateProduct(draft.uuid, {
        name: draft.name,
        description: draft.description ?? undefined,
        status: draft.status,
        approvalNeeded: draft.approvalNeeded,
        ...relationsSelectionToUuids(relations),
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Product</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {loadError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}
        {!loading && (
          <>
            <TextField
              fullWidth
              label="Name"
              margin="normal"
              value={draft?.name ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, name: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              value={draft?.description ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, description: e.target.value } : prev,
                )
              }
            />
            <TextField
              select
              fullWidth
              label="Status"
              margin="normal"
              value={draft?.status ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev
                    ? { ...prev, status: e.target.value as EntityStatus }
                    : prev,
                )
              }
            >
              {Object.values(EntityStatusEnum).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Needs approval"
              margin="normal"
              value={draft?.approvalNeeded === true ? "Yes" : "No"}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev
                    ? { ...prev, approvalNeeded: e.target.value === "Yes" }
                    : prev,
                )
              }
            >
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </TextField>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
              Linked entities
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Associate APIs, categories, FAQs, documents, and links with this
              product. Upload a file to create a library document attached to this product.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={saving || readOnly || !draft?.uuid}
              onClick={() => setUploadOpen(true)}
              sx={{ textTransform: "none", mb: 1 }}
            >
              Upload document to this product
            </Button>
            <ProductRelationsPicker
              value={relations}
              onChange={setRelations}
              disabled={saving || readOnly}
            />

            <ProductGatewaySkuSection
              product={draft}
              onProductUpdated={(updated) => setDraft(updated)}
            />

            {draft?.uuid && (
              <PmgAssignmentSection
                entityUuid={draft.uuid}
                entityType={EntityType.PRODUCT}
                onAccessDenied={setPmgAccessDenied}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || pmgAccessDenied || !draft}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
    <DocumentFileUploadDialog
      open={uploadOpen}
      mode="file"
      productUuids={draft?.uuid ? [draft.uuid] : []}
      onClose={() => setUploadOpen(false)}
      onComplete={(created) => {
        setRelations((prev) => ({
          ...prev,
          documents: [
            ...prev.documents,
            ...created
              .filter((doc) => !prev.documents.some((existing) => existing.uuid === doc.uuid))
              .map((doc) => ({
                uuid: doc.uuid,
                name: doc.name,
                status: doc.status,
              })),
          ],
        }));
      }}
    />
    </>
  );
};

export default EditProductDialog;
